import type { DraftSyncPayload } from "./draftSync";
import type { DraftStyle } from "../components/boardTypes";
import { DEFAULT_STARTER_CONFIG, type StarterConfig, type StarterKey } from "../../shared/components/TeamsBoard";

type SleeperConfig = {
  identifier: string;
};

export type DraftSyncRequest = {
  provider: "sleeper";
  config: SleeperConfig;
};

export const DRAFT_SYNC_STORAGE_KEY = "fantasy-board:draft-sync:v1";

const SLEEPER_PLAYERS_CACHE_KEY = "fantasy-board:sleeper-players:v1";
const SLEEPER_PLAYERS_CACHE_MAX_AGE_MS = 24 * 60 * 60 * 1000;
const SLEEPER_FETCH_TIMEOUT_MS = 5000;

function parseNumber(value: unknown) {
  const number = Number(value);
  return Number.isFinite(number) ? number : undefined;
}

function isReverseRound(roundIndex: number, draftStyle: DraftStyle | undefined) {
  if (draftStyle === "Regular Draft") return false;
  if (draftStyle === "Snake Draft") return roundIndex % 2 === 1;
  if (draftStyle === "Third Round Reversal") return roundIndex === 1 || (roundIndex >= 2 && roundIndex % 2 === 0);
  return false;
}

function getOriginalSlotForPickIndex(pickIndex: number, teams: number, draftStyle: DraftStyle | undefined) {
  const roundIndex = Math.floor(pickIndex / teams);
  const pickOffset = pickIndex % teams;
  return isReverseRound(roundIndex, draftStyle) ? teams - pickOffset : pickOffset + 1;
}

function buildSlotToRosterId(rawValue: unknown) {
  const slotToRosterId = new Map<number, number>();
  if (!rawValue || typeof rawValue !== "object") return slotToRosterId;

  Object.entries(rawValue as Record<string, unknown>).forEach(([slot, rosterId]) => {
    const parsedSlot = parseNumber(slot);
    const parsedRosterId = parseNumber(rosterId);
    if (parsedSlot != null && parsedRosterId != null) {
      slotToRosterId.set(parsedSlot, parsedRosterId);
    }
  });

  return slotToRosterId;
}

function buildSlotToUserId(rawValue: unknown) {
  const slotToUserId = new Map<number, string>();
  if (!rawValue || typeof rawValue !== "object") return slotToUserId;

  Object.entries(rawValue as Record<string, unknown>).forEach(([userId, slot]) => {
    const parsedSlot = parseNumber(slot);
    const normalizedUserId = String(userId ?? "").trim();
    if (parsedSlot != null && normalizedUserId) {
      slotToUserId.set(parsedSlot, normalizedUserId);
    }
  });

  return slotToUserId;
}

function getSleeperUserName(user: any) {
  const metadataTeamName = String(user?.metadata?.team_name ?? "").trim();
  const displayName = String(user?.display_name ?? "").trim();
  const username = String(user?.username ?? "").trim();
  return metadataTeamName || displayName || username;
}

function getSleeperDraftStyle(draft: any): DraftStyle | undefined {
  const draftType = String(draft?.type ?? "").trim().toLowerCase();
  const reversalRound =
    parseNumber(draft?.settings?.reversal_round) ??
    parseNumber(draft?.settings?.third_round_reversal) ??
    parseNumber(draft?.metadata?.reversal_round);

  if (draftType === "snake" && reversalRound === 3) return "Third Round Reversal";
  if (draftType === "snake") return "Snake Draft";
  if (draftType === "linear" || draftType === "regular") return "Regular Draft";
  return undefined;
}

function getSleeperLeagueStarterConfig(league: any): StarterConfig | undefined {
  const rosterPositions = Array.isArray(league?.roster_positions) ? league.roster_positions : [];
  if (!rosterPositions.length) return undefined;

  const next: StarterConfig = {
    ...DEFAULT_STARTER_CONFIG,
    QB: 0,
    RB: 0,
    WR: 0,
    TE: 0,
    Flex: 0,
    Superflex: 0,
    K: 0,
    DST: 0,
  };

  rosterPositions.forEach((rawPosition: unknown) => {
    const normalized = String(rawPosition ?? "").trim().toUpperCase();
    const starterKeyBySleeperPosition: Record<string, StarterKey | undefined> = {
      QB: "QB",
      RB: "RB",
      WR: "WR",
      TE: "TE",
      FLEX: "Flex",
      REC_FLEX: "Flex",
      SUPER_FLEX: "Superflex",
      SUPERFLEX: "Superflex",
      SF: "Superflex",
      K: "K",
      DEF: "DST",
      DST: "DST",
      "D/ST": "DST",
    };
    const key = starterKeyBySleeperPosition[normalized];
    if (key) next[key] += 1;
  });

  return next;
}

export function safeJsonParse<T>(value: string | null, fallback: T): T {
  if (!value) return fallback;
  try {
    return JSON.parse(value) as T;
  } catch {
    return fallback;
  }
}

function buildPlayerName(firstName: unknown, lastName: unknown, fallback: unknown) {
  const joined = `${String(firstName ?? "").trim()} ${String(lastName ?? "").trim()}`.trim();
  return joined || String(fallback ?? "").trim();
}

function isAbortError(error: unknown) {
  return error instanceof DOMException && error.name === "AbortError";
}

async function fetchJsonOrThrow(url: string, init?: RequestInit) {
  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), SLEEPER_FETCH_TIMEOUT_MS);
  const externalSignal = init?.signal;

  if (externalSignal?.aborted) controller.abort();
  const onExternalAbort = () => controller.abort();
  externalSignal?.addEventListener("abort", onExternalAbort, { once: true });

  try {
    const response = await fetch(url, {
      cache: "no-store",
      ...init,
      signal: controller.signal,
    });
    if (!response.ok) {
      const text = await response.text().catch(() => "");
      throw new Error(text || `Request failed with HTTP ${response.status}`);
    }
    return response.json();
  } catch (error) {
    if (isAbortError(error)) {
      throw new Error("Sleeper did not respond. Check your connection and try Sync Draft again.");
    }
    throw error;
  } finally {
    window.clearTimeout(timeoutId);
    externalSignal?.removeEventListener("abort", onExternalAbort);
  }
}

function withCacheBust(url: string) {
  const separator = url.includes("?") ? "&" : "?";
  return `${url}${separator}_t=${Date.now()}`;
}

async function getSleeperPlayersIndex() {
  if (typeof window === "undefined") return {};

  const cached = safeJsonParse<{ savedAt?: number; players?: Record<string, any> }>(
    window.localStorage.getItem(SLEEPER_PLAYERS_CACHE_KEY),
    {}
  );

  if (
    cached.savedAt &&
    Date.now() - cached.savedAt < SLEEPER_PLAYERS_CACHE_MAX_AGE_MS &&
    cached.players &&
    typeof cached.players === "object"
  ) {
    return cached.players;
  }

  const response = await fetchJsonOrThrow("https://api.sleeper.app/v1/players/nfl");
  const players = response && typeof response === "object" ? response : {};

  try {
    window.localStorage.setItem(
      SLEEPER_PLAYERS_CACHE_KEY,
      JSON.stringify({
        savedAt: Date.now(),
        players,
      })
    );
  } catch {}

  return players;
}

function parseSleeperIdentifier(rawValue: string) {
  const trimmed = rawValue.trim();
  if (!trimmed) return "";

  const fromUrl = trimmed.match(/\/(draft|league)\/(?:nfl\/)?([0-9]+)/i) ?? trimmed.match(/([0-9]{6,})/);
  return fromUrl?.[2] ?? fromUrl?.[1] ?? trimmed;
}

export async function loadDraftSyncRequest(request: DraftSyncRequest): Promise<DraftSyncPayload> {
  const identifier = parseSleeperIdentifier(request.config.identifier);
  if (!identifier) throw new Error("Enter a Sleeper draft ID or league ID.");

  let leagueId = "";
  let league: any = null;
  let draft = await fetchJsonOrThrow(
    withCacheBust(`https://api.sleeper.app/v1/draft/${encodeURIComponent(identifier)}`)
  ).catch(() => null);

  if (!draft) {
    league = await fetchJsonOrThrow(
      withCacheBust(`https://api.sleeper.app/v1/league/${encodeURIComponent(identifier)}`)
    );
    leagueId = String(league?.league_id ?? identifier).trim();
    const draftId = String(league?.draft_id ?? "").trim();
    if (!draftId) throw new Error("This Sleeper league does not expose a draft ID.");
    draft = await fetchJsonOrThrow(
      withCacheBust(`https://api.sleeper.app/v1/draft/${encodeURIComponent(draftId)}`)
    );
  }
  leagueId = String(draft?.league_id ?? leagueId).trim();
  if (!league && leagueId) {
    league = await fetchJsonOrThrow(withCacheBust(`https://api.sleeper.app/v1/league/${encodeURIComponent(leagueId)}`)).catch(
      () => null
    );
  }

  const picksResponse = await fetchJsonOrThrow(
    withCacheBust(
      `https://api.sleeper.app/v1/draft/${encodeURIComponent(String(draft.draft_id ?? identifier))}/picks`
    )
  );
  const tradedPicksResponse = await fetchJsonOrThrow(
    withCacheBust(
      `https://api.sleeper.app/v1/draft/${encodeURIComponent(String(draft.draft_id ?? identifier))}/traded_picks`
    )
  ).catch(() => []);
  const picks = Array.isArray(picksResponse) ? picksResponse : [];
  const tradedPicks = Array.isArray(tradedPicksResponse) ? tradedPicksResponse : [];
  const sleeperPlayersById = picks.length ? await getSleeperPlayersIndex() : {};
  const [leagueUsersResponse, leagueRostersResponse] = leagueId
    ? await Promise.all([
        fetchJsonOrThrow(withCacheBust(`https://api.sleeper.app/v1/league/${encodeURIComponent(leagueId)}/users`)).catch(() => []),
        fetchJsonOrThrow(withCacheBust(`https://api.sleeper.app/v1/league/${encodeURIComponent(leagueId)}/rosters`)).catch(() => []),
      ])
    : [[], []];
  const leagueUsers = Array.isArray(leagueUsersResponse) ? leagueUsersResponse : [];
  const leagueRosters = Array.isArray(leagueRostersResponse) ? leagueRostersResponse : [];

  const teamCount =
    parseNumber(draft?.settings?.teams) ??
    parseNumber(draft?.settings?.teams_num) ??
    parseNumber(draft?.metadata?.teams) ??
    parseNumber(draft?.draft_order && Object.keys(draft.draft_order).length);

  const rounds = parseNumber(draft?.settings?.rounds);
  const draftStyle = getSleeperDraftStyle(draft);
  const rosterStarterConfig = getSleeperLeagueStarterConfig(league);
  const normalizedTeams = teamCount ? Math.max(2, Math.min(20, Math.round(teamCount))) : undefined;
  const normalizedRounds = rounds ? Math.max(1, Math.min(40, Math.round(rounds))) : undefined;
  const slotToRosterId = buildSlotToRosterId(draft?.slot_to_roster_id);
  const slotToUserId = buildSlotToUserId(draft?.draft_order);
  const teamIndexByRosterId = new Map<number, number>();
  slotToRosterId.forEach((rosterId, slot) => {
    teamIndexByRosterId.set(rosterId, slot - 1);
  });
  const userById = new Map<string, any>();
  leagueUsers.forEach((user: any) => {
    const userId = String(user?.user_id ?? "").trim();
    if (userId) userById.set(userId, user);
  });
  const rosterById = new Map<number, any>();
  leagueRosters.forEach((roster: any) => {
    const rosterId = parseNumber(roster?.roster_id);
    if (rosterId != null) rosterById.set(rosterId, roster);
  });

  const teamNames = normalizedTeams
    ? Array.from({ length: normalizedTeams }, (_, index) => {
        const slot = index + 1;
        const rosterId = slotToRosterId.get(slot);
        const rosterOwnerId = rosterId != null ? String(rosterById.get(rosterId)?.owner_id ?? "").trim() : "";
        const draftOrderUserId = slotToUserId.get(slot) ?? "";
        const user = userById.get(rosterOwnerId) ?? userById.get(draftOrderUserId);
        return getSleeperUserName(user) || `Team ${slot}`;
      })
    : undefined;

  const ownerRosterIdByPickIndex = new Map<number, number>();
  if (normalizedTeams && normalizedRounds && slotToRosterId.size) {
    for (let pickIndex = 0; pickIndex < normalizedTeams * normalizedRounds; pickIndex += 1) {
      const round = Math.floor(pickIndex / normalizedTeams) + 1;
      const originalSlot = getOriginalSlotForPickIndex(pickIndex, normalizedTeams, draftStyle);
      const originalRosterId = slotToRosterId.get(originalSlot);
      if (originalRosterId == null) continue;

      const tradedPick = tradedPicks.find((pick: any) => {
        const tradedRound = parseNumber(pick?.round);
        const tradedRosterId = parseNumber(pick?.roster_id);
        const tradedDraftSlot = parseNumber(pick?.draft_slot);
        return tradedRound === round && (tradedDraftSlot === originalSlot || tradedRosterId === originalRosterId);
      });
      const currentOwnerId = parseNumber(tradedPick?.owner_id) ?? originalRosterId;
      ownerRosterIdByPickIndex.set(pickIndex, currentOwnerId);
    }
  }

  picks.forEach((pick: any) => {
    const pickNumber = parseNumber(pick?.pick_no);
    const ownerRosterId = parseNumber(pick?.roster_id);
    if (pickNumber != null && pickNumber > 0 && ownerRosterId != null) {
      ownerRosterIdByPickIndex.set(pickNumber - 1, ownerRosterId);
    }
  });

  const draftSlotTeamIndexes =
    normalizedTeams && normalizedRounds
      ? Array.from({ length: normalizedTeams * normalizedRounds }, (_, pickIndex) => {
          const ownerRosterId = ownerRosterIdByPickIndex.get(pickIndex);
          const teamIndex = ownerRosterId != null ? teamIndexByRosterId.get(ownerRosterId) : undefined;
          return teamIndex != null && teamIndex >= 0 && teamIndex < normalizedTeams ? teamIndex : null;
        })
      : undefined;

  return {
    provider: "sleeper",
    sourceLabel: `Sleeper ${draft?.metadata?.name ? `• ${draft.metadata.name}` : `• ${draft?.draft_id ?? identifier}`}`,
    teams: teamCount,
    rounds,
    draftStyle,
    teamNames,
    rosterStarterConfig,
    draftSlotTeamIndexes,
    picks: picks.map((pick: any) => {
      const sleeperPlayer = sleeperPlayersById[String(pick?.player_id ?? "")] ?? {};
      return {
        pickNumber: parseNumber(pick?.pick_no) ?? 0,
        round: parseNumber(pick?.round),
        rosterIndex: parseNumber(pick?.draft_slot),
        playerName: buildPlayerName(
          sleeperPlayer?.first_name ?? pick?.metadata?.first_name,
          sleeperPlayer?.last_name ?? pick?.metadata?.last_name,
          sleeperPlayer?.full_name ?? pick?.metadata?.player_name
        ),
        playerId: String(pick?.player_id ?? ""),
        team: String(sleeperPlayer?.team ?? sleeperPlayer?.team_abbr ?? pick?.metadata?.team ?? ""),
        position: String(sleeperPlayer?.position ?? pick?.metadata?.position ?? ""),
      };
    }),
  };
}
